class HeaderMixedMenu extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('.header-mixed-menu__overlay');
    this.topLevelDetails = this.querySelectorAll('.header-mixed-menu__details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    this.overlay?.addEventListener('click', () => this.closeAllTopLevelMenus());
  }

  isTopLevelDetails(detailsElement) {
    return detailsElement?.classList.contains('header-mixed-menu__details');
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    this.isTopLevelDetails(openDetailsElement)
      ? this.closeTopLevelMenu(openDetailsElement)
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const addTrapFocus = () => {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    };

    if (this.isTopLevelDetails(detailsElement)) {
      if (isOpen) {
        event.preventDefault();
        this.closeTopLevelMenu(detailsElement, summaryElement);
      } else {
        this.topLevelDetails.forEach((details) => {
          if (details === detailsElement || !details.hasAttribute('open')) return;
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
          details.querySelector('summary')?.setAttribute('aria-expanded', false);
        });
        setTimeout(() => {
          this.openTopLevelMenu(detailsElement, summaryElement);
        }, 0);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openTopLevelMenu(detailsElement, summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom')
        ? 1
        : 0;

    const bottom = parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset);
    document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${bottom}px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    this.header?.classList.add('menu-open');
    this.classList.add('menu-open');

    setTimeout(() => {
      detailsElement.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(detailsElement, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeTopLevelMenu(detailsElement, elementToFocus = false) {
    const summaryElement = detailsElement.querySelector('summary');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    detailsElement.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    this.closeAnimation(detailsElement, () => {
      if (!this.querySelector('.header-mixed-menu__details[open]')) {
        this.header?.classList.remove('menu-open');
        this.classList.remove('menu-open');
        document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
        window.removeEventListener('resize', this.onResize);
      }
      if (elementToFocus) {
        removeTrapFocus(elementToFocus);
        elementToFocus.setAttribute('aria-expanded', false);
      }
    });
  }

  closeAllTopLevelMenus() {
    this.topLevelDetails.forEach((details) => {
      if (details.hasAttribute('open')) {
        this.closeTopLevelMenu(details, details.querySelector('summary'));
      }
    });
  }

  onFocusOut() {
    setTimeout(() => {
      const openTopLevel = this.querySelector('.header-mixed-menu__details[open]');
      if (openTopLevel && !openTopLevel.contains(document.activeElement) && !this.contains(document.activeElement)) {
        this.closeAllTopLevelMenus();
      }
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    if (this.isTopLevelDetails(detailsElement)) {
      this.closeTopLevelMenu(detailsElement, detailsElement.querySelector('summary'));
    } else {
      this.closeSubmenu(detailsElement);
    }
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement, onComplete) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        onComplete?.();
        const openParent = detailsElement.closest('details[open]');
        if (openParent) {
          trapFocus(openParent, openParent.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  onResize = () => {
    if (!this.header) return;
    const bottom = parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset);
    document.documentElement.style.setProperty('--header-bottom-position', `${bottom}px`);
    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${bottom}px`);
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-mixed-menu', HeaderMixedMenu);
